// @ts-strict-ignore
import React, {useEffect, useState} from 'react';
import clsx from 'clsx';

import styles from 'common/query-builder/chart-customization/index.module.styl';
import {ButtonReact} from 'common/components/button';
import {ButtonLayout, ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {useTranslation} from 'common/i18n/hooks';
import {InputReact} from 'common/components/input';
import {InputSize} from 'common/components/input/types';
import {ToggleSize, ToggleTheme} from 'common/components/toggle/types';
import {ToggleReact} from 'common/components/toggle';
import {formatMetricNumberWithUnits} from 'common/util';
import {DropMenuReact} from 'common/components/drop-menu';
import {Directions, PositioningStrategy} from 'common/components/drop-menu/types';
import {
  AxisUnitOptions,
  AxisUnitPrecision,
  BEHAVIOR_METRIC_ABBREVIATION_DEFAULT,
  BehaviorMetricDisplay,
  BookmarkYAxisOptions,
} from 'common/report/insights/util/types';
import {ChartType, InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {
  displayOptionsHaveChanged,
  getDisplayOptionsFromLineChartYAxisOptions,
  getMetricFormattingDefaultsFromQueryEntry,
  mergeDisplayOptions,
} from 'common/report/insights/charts/util';
import {EntryWithDisplay} from 'common/query-builder/query-entry-section/types';
import {AxisAssignment} from 'common/charts/types';

import {CLEAR_VALUE, PREFIX_SUFFIX_MAX_CHAR_COUNT, UNSET_VALUE} from '../axis/constants';
import {CallingLocation, track, TrackingEvent} from '../tracking';
import {ALL_PRESETS, CUSTOM_PRESET, DEFAULT_PRESET, PREFIX_PRESETS, PREVIEW_VALUE, SUFFIX_PRESETS} from './constants';
import {DecimalOption} from './decimal-option';
import {getToggleValue} from './util';

const getButtonValue = (prefix?: string, suffix?: string) => {
  return prefix?.[0] || suffix?.[0] || DEFAULT_PRESET;
};

const DecimalRow = ({
  currentDisplay,
  defaults,
  entry,
  setEntry,
  setAxis,
}: {
  currentDisplay: BehaviorMetricDisplay;
  defaults?: BehaviorMetricDisplay;
  entry: EntryWithDisplay;
  setEntry: (entry: EntryWithDisplay) => void;
  setAxis?: (axis: BookmarkYAxisOptions) => void;
}) => {
  const {t} = useTranslation();

  const precision = currentDisplay.precision ?? defaults?.precision ?? CLEAR_VALUE;
  const setPrecision = (newPrecision: AxisUnitPrecision) => {
    if (setEntry) {
      const newClause = {...entry};
      newClause.display = {...(entry.display || {}), precision: newPrecision};
      setEntry(newClause);
    } else {
      setAxis?.({axisUnit: {precision: newPrecision}});
    }
  };
  return (
    <div className={clsx(styles.row, entry ? styles.short : undefined)}>
      <div style={{gridColumn: 1}}>
        <label className={styles.ccSublabel}>{t(`chart-customization.decimals`)}</label>
      </div>
      <div style={{gridColumn: 2}}>
        <DecimalOption precision={precision} setPrecision={setPrecision} isLeft />
      </div>
      <div style={{gridColumn: 3}}>
        <DecimalOption precision={precision} setPrecision={setPrecision} isLeft={false} />
      </div>
    </div>
  );
};

const AbbrevRow = ({
  currentDisplay,
  entry,
  setEntry,
  setAxis,
}: {
  currentDisplay: BehaviorMetricDisplay;
  entry: EntryWithDisplay;
  setEntry: (clause: EntryWithDisplay) => void;
  setAxis: (axis: BookmarkYAxisOptions) => void;
}) => {
  const {t} = useTranslation();
  return (
    <div className={clsx(styles.row, entry ? styles.short : undefined)}>
      <div style={{gridColumn: 1}}>
        <label className={styles.ccSublabel}>{t(`chart-customization.abbreviation`)}</label>
      </div>
      <div className={styles.singleInputColumn}>
        <ToggleReact
          className={styles.singleInput}
          size={ToggleSize.Medium}
          theme={ToggleTheme.SegmentedController}
          options={[
            {label: t(`common.yes`), value: `true`},
            {label: t(`common.no`), value: `false`},
          ]}
          selected={String(currentDisplay.abbrev ?? BEHAVIOR_METRIC_ABBREVIATION_DEFAULT)}
          onSelect={(ev) => {
            if (setEntry) {
              const newEntry = {...entry};
              newEntry.display = {...(entry.display || {}), abbrev: ev.detail.selected === `true`};
              setEntry(newEntry);
            } else {
              setAxis?.({axisUnit: {abbrev: ev.detail.selected === `true`}});
            }
          }}
        />
      </div>
    </div>
  );
};

const PreviewRow = ({
  finalDisplay,
  entry,
  onReset,
  onResetShown = true,
}: {
  finalDisplay: BehaviorMetricDisplay;
  entry: EntryWithDisplay;
  onReset: (() => void) | null;
  onResetShown: boolean;
}) => {
  const {t} = useTranslation();
  const previewValue = formatMetricNumberWithUnits(PREVIEW_VALUE, finalDisplay);
  return (
    <div className={clsx(styles.row, entry ? styles.short : undefined)}>
      <div style={{gridColumn: 1}}>
        <label className={styles.ccSublabel}>{t(`chart-customization.preview`)}</label>
      </div>
      <div className={styles.chartCustomizationPreview} style={{gridColumn: `2 / span 2`}}>
        <div className={styles.chartCustomizationPreviewText} style={{flex: `auto`}} title={previewValue}>
          {previewValue}
        </div>
        <div className={styles.chartCustomizationResetButton}>
          {onReset && (
            <ButtonReact
              class={onResetShown ? `` : styles.hideResetButton}
              disabled={!onResetShown}
              layout={ButtonLayout.Horizontal}
              onClick={() => onReset()}
              size={ButtonSize.Small}
              theme={ButtonTheme.Tertiary}
            >
              {t(`chart-customization.reset`)}
            </ButtonReact>
          )}
        </div>
      </div>
    </div>
  );
};

const MetricUnitRow = ({
  elref,
  currentDisplay,
  defaults,
  entry,
  setEntry,
  setAxis,
}: {
  elref?: string;
  currentDisplay: BehaviorMetricDisplay;
  defaults?: BehaviorMetricDisplay;
  entry?: EntryWithDisplay;
  setEntry?: (clause: EntryWithDisplay) => void;
  setAxis?: (axis: BookmarkYAxisOptions) => void;
}) => {
  const {t} = useTranslation();
  return (
    <div className={clsx(styles.row, entry ? styles.short : undefined)} elref={elref}>
      <div style={{gridColumn: 1}}>
        <label className={styles.ccSublabel}>
          {t(`chart-customization.prefix`)} / {t(`chart-customization.suffix`)}
        </label>
      </div>
      <div style={{gridColumn: 2}}>
        <InputReact
          elref={`customPrefixInput`}
          ariaLabel={t(`chart-customization.prefix`)}
          className={styles.twoInputColumn}
          maxlength={PREFIX_SUFFIX_MAX_CHAR_COUNT}
          size={InputSize.Medium}
          placeholder={defaults?.prefix ?? t(`chart-customization.prefix`)}
          value={currentDisplay.prefix || ``}
          onChange={(ev) => {
            if (setEntry) {
              const newEntry = {...entry};
              newEntry.display = {...(entry.display || {}), prefix: ev.detail.value || UNSET_VALUE};
              setEntry(newEntry);
            } else {
              setAxis?.({axisUnit: {prefix: ev.detail.value || UNSET_VALUE}});
            }
          }}
        />
      </div>
      <div style={{gridColumn: 3}}>
        <InputReact
          elref={`customSuffixInput`}
          ariaLabel={t(`chart-customization.suffix`)}
          className={styles.twoInputColumn}
          maxlength={PREFIX_SUFFIX_MAX_CHAR_COUNT}
          size={InputSize.Medium}
          placeholder={defaults?.suffix ?? t(`chart-customization.suffix`)}
          value={currentDisplay.suffix || ``}
          onChange={(ev) => {
            if (setEntry) {
              const newEntry = {...entry};
              newEntry.display = {...(entry.display || {}), suffix: ev.detail.value || UNSET_VALUE};
              setEntry(newEntry);
            } else {
              setAxis?.({axisUnit: {suffix: ev.detail.value || UNSET_VALUE}});
            }
          }}
        />
      </div>
    </div>
  );
};

export function MetricUnitsButton(props: {
  entry: EntryWithDisplay;
  propertyDisplay?: BehaviorMetricDisplay;
  insideFooter?: boolean;
  updateOpen: () => void;
  size?: ButtonSize;
  style?: React.CSSProperties;
}) {
  const defaultsFromEntry = getMetricFormattingDefaultsFromQueryEntry(props.entry);
  const mergedOptions: BehaviorMetricDisplay = mergeDisplayOptions(
    defaultsFromEntry,
    props.propertyDisplay,
    props.entry.display,
  );

  return (
    <div className={styles.metricUnitsButton}>
      <ButtonReact
        className={styles.unitSelectionButton}
        theme={ButtonTheme.Tertiary}
        size={props.size || ButtonSize.Small}
        style={{
          '--button-content-width': props.insideFooter ? `reset` : undefined,
          '--button-justify-content': `center`,
          ...(props.style || {}),
        }}
        layout={ButtonLayout.Horizontal}
        onClick={() => props.updateOpen()}
      >
        {getButtonValue(mergedOptions.prefix, mergedOptions.suffix)}
      </ButtonReact>
    </div>
  );
}

export function MetricUnitsSelectorDropdown(props: {
  callingLocation: CallingLocation;
  entry: EntryWithDisplay;
  setEntry: (entry: EntryWithDisplay) => void;
  chartType?: ChartType;
  propertyDisplay?: BehaviorMetricDisplay;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  return (
    <DropMenuReact
      // 340px fits the preview + reset button with {abbrev: false, precision: 8}
      style={{width: `340px`}}
      open={props.open}
      offset={12}
      positionStrategy={PositioningStrategy.Fixed}
      direction={Directions.Down}
      forceStacking
      onChange={(ev) => {
        const opened = ev.detail.state === `open`;
        props.setOpen(opened);
        if (opened) {
          track(TrackingEvent.OpenedMenu, props.callingLocation, {metricEntry: props.entry});
        }
      }}
    >
      <div className={styles.metricSettingsBlock}>
        <MetricUnitsSelector
          callingLocation={props.callingLocation}
          entry={props.entry}
          setEntry={props.setEntry}
          chartType={props.chartType}
        />
      </div>
    </DropMenuReact>
  );
}

export function MetricUnitsSelector(props: {
  axis?: BookmarkYAxisOptions;
  axisAssignment?: AxisAssignment;
  bookmarkParams?: InsightsBookmarkParams;
  chartType?: ChartType;
  callingLocation: CallingLocation;
  defaultAxisOptions?: AxisUnitOptions;
  elref?: string;
  entry?: EntryWithDisplay;
  propertyDisplay?: BehaviorMetricDisplay;
  setEntry?: (entry: EntryWithDisplay) => void;
  setAxis?: (axis: BookmarkYAxisOptions) => void;
}) {
  const {t} = useTranslation();

  const defaultsFromEntry = props.entry ? getMetricFormattingDefaultsFromQueryEntry(props.entry, props.chartType) : {};
  // The inherited state from metric, property, or formula.
  const inheritedDisplay = props.entry
    ? mergeDisplayOptions({}, defaultsFromEntry, props.propertyDisplay)
    : props.defaultAxisOptions || {};
  // The actual current state.
  const currentDisplay =
    (props.entry ? props.entry.display : getDisplayOptionsFromLineChartYAxisOptions(props.axis)) || {};
  // The final merged state used for formatting.
  const finalMergedDisplay: BehaviorMetricDisplay = mergeDisplayOptions({}, inheritedDisplay, currentDisplay);
  const changedOptionPresent = displayOptionsHaveChanged({current: finalMergedDisplay, defaults: inheritedDisplay});

  const [preset, setPreset] = useState<string>();

  const setEntry = props.setEntry
    ? (entry: EntryWithDisplay) => {
        track(TrackingEvent.UpdatedUnits, CallingLocation.Metric, {metricEntry: entry});
        props.setEntry(entry);
      }
    : null;
  const setAxis = props.setAxis
    ? (axis: BookmarkYAxisOptions) => {
        track(TrackingEvent.UpdatedUnits, CallingLocation.Axis, {axis, axisAssignment: props.axisAssignment});
        props.setAxis(axis);
      }
    : null;

  useEffect(() => {
    setPreset((x) => {
      if (x === CUSTOM_PRESET) {
        return x;
      }
      return getToggleValue(finalMergedDisplay.prefix, finalMergedDisplay.suffix);
    });
  }, [finalMergedDisplay.prefix, finalMergedDisplay.suffix, props.axis]);

  // Reset for axis is handled in the axis component
  const onReset = setEntry
    ? () => {
        track(TrackingEvent.Reset, props.callingLocation, {metricEntry: props.entry});
        const newEntry = {...props.entry};
        newEntry.display = {};
        setEntry(newEntry);
      }
    : null;

  return (
    <div elref={props.elref}>
      <div className={clsx(styles.row, props.entry ? styles.short : undefined)}>
        <div style={{gridColumn: 1}}>
          <label className={styles.ccSublabel}>{t(`chart-customization.axis-unit`)}</label>
        </div>
        <div className={styles.singleInputColumn} elref='metricUnitsToggle' style={{gridColumn: `2 / span 2`}}>
          <ToggleReact
            className={styles.singleInput}
            size={ToggleSize.Medium}
            theme={ToggleTheme.SegmentedController}
            options={ALL_PRESETS.map((unit) => ({label: unit, value: unit}))}
            selected={preset}
            onSelect={(ev) => {
              const selected = ev.detail.selected;
              setPreset(selected);

              let newPrefix = props.entry?.display?.prefix;
              let newSuffix = props.entry?.display?.suffix;
              if (PREFIX_PRESETS.includes(selected)) {
                newPrefix = ev.detail.selected;
                newSuffix = CLEAR_VALUE;
              } else if (SUFFIX_PRESETS.includes(selected)) {
                newPrefix = CLEAR_VALUE;
                newSuffix = ev.detail.selected;
              } else if (selected === DEFAULT_PRESET) {
                // When they select the default preset (#) we clear the values.
                newPrefix = CLEAR_VALUE;
                newSuffix = CLEAR_VALUE;
              } else if (selected === CUSTOM_PRESET) {
                // For Custom we use the unset value because we want inherited
                // values to take precedence until the user explicitly specifies
                // a custom unit.
                newPrefix = UNSET_VALUE;
                newSuffix = UNSET_VALUE;
              }
              if (setAxis) {
                const newAxis = {axisUnit: {prefix: newPrefix, suffix: newSuffix}};
                setAxis(newAxis);
                track(TrackingEvent.SelectedPreset, CallingLocation.Axis, {
                  axis: newAxis,
                  axisAssignment: props.axisAssignment,
                  selectedPreset: selected,
                });
              } else {
                const newEntry = {
                  ...props.entry,
                  display: {
                    ...props.entry.display,
                    prefix: newPrefix,
                    suffix: newSuffix,
                  },
                };
                setEntry?.(newEntry);
                track(TrackingEvent.SelectedPreset, CallingLocation.Metric, {
                  metricEntry: newEntry,
                  selectedPreset: selected,
                });
              }
            }}
          />
        </div>
      </div>
      {preset === CUSTOM_PRESET && (
        <MetricUnitRow
          elref={`customPresetRow`}
          currentDisplay={currentDisplay}
          defaults={inheritedDisplay}
          entry={props.entry}
          setEntry={setEntry}
          setAxis={setAxis}
        />
      )}
      <AbbrevRow currentDisplay={currentDisplay} entry={props.entry} setEntry={setEntry} setAxis={setAxis} />
      <DecimalRow
        currentDisplay={currentDisplay}
        defaults={inheritedDisplay}
        entry={props.entry}
        setEntry={setEntry}
        setAxis={setAxis}
      />
      <PreviewRow
        finalDisplay={finalMergedDisplay}
        entry={props.entry}
        onReset={onReset}
        onResetShown={changedOptionPresent}
      />
    </div>
  );
}
